import React, { ChangeEvent, useCallback, useRef, useState } from 'react';

import {
  Box,
  Button,
  buttonClasses,
  ClickAwayListener,
  FormControl,
  FormHelperText,
  Grow,
  InputLabel,
  inputLabelClasses,
  ListSubheader,
  listSubheaderClasses,
  MenuItem,
  menuItemClasses,
  MenuList,
  Paper,
  Popper,
  styled,
} from '@mui/material';

import { CheckmarkIcon, SelectArrowDownIcon } from '../../../assets/icons';
import { scrollToDropdown } from './dropdown-scroll';

type DropdownItem = {
  name: string;
  value: string;
  index: number;
};

type DropdownProps = {
  idToScroll?: string;
  items: Record<string, DropdownItem[]>;
  id: string;
  onSelect: (index: number) => void;
  onClose?: () => void;
  activeItem: number | null;
  label: string;
  error?: boolean;
  helperText?: React.ReactNode;
};

export const Dropdown = ({
  idToScroll,
  items,
  id,
  onSelect,
  activeItem,
  label,
  error,
  helperText,
  onClose,
}: DropdownProps) => {
  const menuRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setOpen] = useState(false);

  const flattedOptions = Object.values(items).flat(2);

  const selectedValue =
    activeItem !== null ? flattedOptions[activeItem].name : undefined;
  const useFocusedStyle = isOpen || !!selectedValue;

  const onIndustrySelectionChange = useCallback(
    (evt: ChangeEvent<HTMLSelectElement>) => {
      const val = evt.target.value;
      let idx = flattedOptions.findIndex((opt) => opt.value === val);
      idx = idx >= 0 ? idx : 0;

      onSelect(idx);
    },
    [onSelect, flattedOptions],
  );

  const scrollToDropdownRef = useRef(
    scrollToDropdown(idToScroll ?? id, flattedOptions, {
      dropdownButtonHeight: 56,
    }),
  );

  return (
    <Box sx={{ position: 'relative' }}>
      <DropdownFormControl
        variant="filled"
        focused={useFocusedStyle}
        fullWidth
        error={error}
        id={id}
      >
        <MenuButton
          id={`${id}-button`}
          aria-labelledby={`${id}-button-label`}
          endIcon={<SelectArrowDownIcon />}
          variant="contained"
          ref={menuRef}
          onClick={() => {
            if (isOpen === false && idToScroll) {
              scrollToDropdownRef.current();
            }

            setOpen(!isOpen);
          }}
          sx={{
            [`&.${buttonClasses.root}`]: {
              borderColor: error
                ? 'var(--color-light-red)'
                : isOpen
                  ? 'var(--color-dark-coal)'
                  : '',
            },

            svg: {
              transform: `rotate(${isOpen ? 180 : 0}deg)`,
            },
          }}
        >
          {selectedValue}
        </MenuButton>

        {/* put label after menu button so we can style it using the 'next sibling' css selector */}
        {selectedValue === undefined && (
          <InputLabel id={`${id}-button-label`} htmlFor={`${id}-button`}>
            {label}
          </InputLabel>
        )}

        {/* the native select overlay is only used on mobile viewports */}
        <NativeSelectOverlay
          onChange={onIndustrySelectionChange}
          aria-labelledby={`${id}-button-label`}
        >
          {flattedOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.name}
            </option>
          ))}
        </NativeSelectOverlay>
        <DropdownMenu
          items={items}
          open={isOpen}
          anchor={menuRef.current}
          onClose={() => {
            if (onClose) {
              onClose();
            }
            setOpen(false);
          }}
          onSelect={(newIndex) => {
            setOpen(false);
            onSelect(newIndex);
          }}
          name={`${id}-menu`}
          labelledby={`${id}-button`}
          activeItem={activeItem}
          error={error}
        />
        <FormHelperText sx={{ marginLeft: '0' }}>{helperText}</FormHelperText>
      </DropdownFormControl>
    </Box>
  );
};

const DropdownFormControl = styled(FormControl)(({ theme }) => ({
  [`.${inputLabelClasses.root}`]: {
    zIndex: 'auto',
  },

  [`.${inputLabelClasses.filled}`]: {
    ...theme.typography.footnote,
    color: 'var(--color-medium-grey)',
    transform: 'translate(16px, 18px) scale(1)',

    [`&.${inputLabelClasses.error}`]: {
      color: 'var(--color-light-red)',
    },

    [`&.${inputLabelClasses.focused}:not(.${inputLabelClasses.error})`]: {
      color: 'var(--color-medium-grey)',
    },
  },
}));

const MenuButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'white !important', // don't let hovers overwrite the bg color
  display: 'flex',
  justifyContent: 'flex-start',
  color: 'var(--color-dark-coal) !important',
  textAlign: 'left',
  transition: 'border-color 0.2s ease',
  width: '100%',
  height: '56px',
  padding: '16px 12px 15px 16px !important',

  [`&.${buttonClasses.root}`]: {
    ...theme.typography.body,
    fontWeight: theme.typography.fontWeightRegular,
    border: '1px solid var(--color-medium-grey)',
  },

  '&:focus, &:hover, &:has(~ select:focus)': {
    // use the outline instead of border to prevent the page from jumping
    outline: '2px solid var(--color-dark-coal)',
    outlineOffset: '-1px',
  },

  [`.${buttonClasses.endIcon}`]: {
    position: 'absolute',
    right: '20px',
    top: '24px',
    margin: 0,
  },

  [`.${buttonClasses.icon}`]: {
    top: '50%',
    transform: 'translateY(-50%)',
    color: 'var(--color-medium-grey)',

    svg: {
      transition:
        'transform var(--transition-duration) var(--transition-timing)',
    },
  },

  svg: {},
}));

const NativeSelectOverlay = styled('select')(({ theme }) => ({
  WebkitAppearance: 'none', // required for safari to modify height
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  opacity: 0,
  border: 0,

  [theme.breakpoints.up('sm')]: {
    display: 'none',
  },
}));

type DropdownMenuProps = {
  items: Record<string, DropdownItem[]>;
  open: boolean;
  name: string;
  labelledby: string;
  anchor: HTMLButtonElement | null;
  onClose: () => void;
  onSelect: (index: number) => void;
  activeItem: number | null;
  error?: boolean;
};

const DropdownMenu = ({
  open,
  items,
  anchor,
  onClose,
  onSelect,
  name,
  labelledby,
  activeItem,
  error,
}: DropdownMenuProps) => (
  <DropdownPopper
    open={open}
    anchorEl={anchor}
    role={undefined}
    placement="bottom-start"
    transition
    disablePortal
  >
    {({ TransitionProps }) => (
      <Grow
        {...TransitionProps}
        style={{
          transformOrigin: 'center top',
          width: '100%',
          inset: 'unset !important',
        }}
      >
        <DropdownPaper isError={error}>
          <ClickAwayListener onClickAway={onClose}>
            <DropdownMenuList
              variant="selectedMenu"
              id={name}
              aria-labelledby={labelledby}
            >
              {Object.entries(items).map(([groupName, group]) => [
                groupName ? (
                  <StyleListSubheader>{groupName}</StyleListSubheader>
                ) : null,
                group.map((item) => (
                  <DropdownMenuItem
                    disableRipple
                    key={item.name}
                    onClick={() => onSelect(item.index)}
                    isActive={item.index === activeItem}
                  >
                    {item.name}
                    {item.index === activeItem && (
                      <CheckmarkIcon
                        style={{ color: 'var(--color-light-red)' }}
                      />
                    )}
                  </DropdownMenuItem>
                )),
              ])}
            </DropdownMenuList>
          </ClickAwayListener>
        </DropdownPaper>
      </Grow>
    )}
  </DropdownPopper>
);

const DropdownPopper = styled(Popper)({
  inset: 'unset !important',
  width: '100%',
  transform: 'translate(0px, 55px) !important', // align with border of parent container
  zIndex: 1,
});

const DropdownPaper = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'isError',
})((props: { isError?: boolean }) => ({
  backgroundColor: 'var(--color-white)',
  boxShadow: `
    inset -1px -1px 0px 0px var(${
      props.isError ? '--color-light-red' : '--color-dark-coal'
    }),
    inset  1px  0px 0px 0px var(${
      props.isError ? '--color-light-red' : '--color-dark-coal'
    })`,
}));

const DropdownMenuList = styled(MenuList)({
  paddingTop: '5px',
  paddingBottom: '5px',

  '&:focus': {
    outline: 'none',
  },

  '&::after': {
    content: '" "',
    display: 'block',
    height: '0',
    backgroundColor: 'red',
    position: 'absolute',
    top: 0,
    left: '14px',
    right: '14px',
    borderTop: '1px solid var(--color-medium-light-grey)',
  },
});

const StyleListSubheader = styled(ListSubheader)(({ theme }) => ({
  [`&.${listSubheaderClasses.root}`]: {
    ...theme.typography.body,
    lineHeight: '32px',
    color: 'var(--color-dark-coal)',
    fontWeight: theme.typography.fontWeightMedium,

    paddingTop: '9px',
    paddingBottom: '9px',
    paddingLeft: '16px',
    paddingRight: '16px',
    whiteSpace: 'normal',

    backgroundColor: 'transparent',
  },
}));

const DropdownMenuItem = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})((props: { isActive?: boolean }) => ({ theme }) => ({
  ...theme.typography.body,

  paddingTop: '9px',
  paddingBottom: '9px',
  paddingLeft: '30px',
  paddingRight: '16px',
  whiteSpace: 'normal',

  color: props.isActive ? 'var(--color-light-red)' : undefined,

  '&:hover, &:focus': {
    color: 'var(--color-light-red)',
    backgroundColor: 'transparent',
  },

  [`&.${menuItemClasses.focusVisible}`]: {
    color: 'var(--color-light-red)',

    '&:hover': {
      backgroundColor: 'transparent',
    },
  },

  svg: {
    marginLeft: '10px',
  },
}));
